import { z } from 'zod';
import {
  LocaleSchema,
  MoneySchema,
  ShortIdSchema,
  SlugSchema,
  TimestampSchema,
  UidSchema,
} from './primitives.js';

/**
 * Sprint 80 — recruiter epic foundation. The fourth-layer persona on
 * the platform per [[project-agent-recruiter-layer]]: recruiters sign
 * up agents, agents sign up operators, and the recruiter earns a
 * configurable share of the agent's commissions for that operator's
 * lifetime.
 *
 * Shape deliberately mirrors `AgentProfileSchema` so the same patterns
 * (status pill, tier, KYC, payout, overrides) apply. One day this file
 * + agent.ts may collapse into a single `SalesPersonSchema` discriminated
 * by `kind: 'agent' | 'recruiter'`; not worth doing until both layers
 * have shipped enough surface area to know what's truly shared.
 */

export const RecruiterStatusSchema = z.enum([
  'applied',
  'pending_kyc',
  'active',
  'suspended',
  'terminated',
]);
export type RecruiterStatus = z.infer<typeof RecruiterStatusSchema>;

export const RecruiterTierSchema = z.enum(['bronze', 'silver', 'gold']);
export type RecruiterTier = z.infer<typeof RecruiterTierSchema>;

export const RecruiterPayoutDetailsSchema = z.object({
  bankName: z.string().optional(),
  accountName: z.string().optional(),
  accountNumber: z.string().optional(),
  duitNowId: z.string().optional().describe('NRIC or mobile number for DuitNow'),
  thresholdSen: MoneySchema.default(10000).describe('Minimum payout threshold; default RM 100'),
});

export const RecruiterProfileSchema = z.object({
  id: ShortIdSchema,
  platformBrandId: SlugSchema.describe(
    'Which platform brand this recruiter operates under (ADR-0017). Immutable after signup.',
  ),
  uid: UidSchema,
  name: z.string(),
  phone: z.string(),
  email: z.string().email(),
  locale: LocaleSchema.default('en'),
  status: RecruiterStatusSchema,
  tier: RecruiterTierSchema.default('bronze'),
  /**
   * Recruiter referral code shape: 2-letter prefix `RC` + 4-6 alphanumeric
   * suffix (e.g., `RC-7K2P`). Distinct from agent code prefix so a code
   * misread can't accidentally cross channels.
   */
  code: z
    .string()
    .regex(/^RC-[A-Z0-9]{4,6}$/)
    .describe('Recruiter referral code'),
  payout: RecruiterPayoutDetailsSchema,
  kyc: z
    .object({
      submittedAt: TimestampSchema.optional(),
      verifiedAt: TimestampSchema.optional(),
      verifierUid: UidSchema.optional(),
      icScanRef: z.string().optional(),
    })
    .default({}),
  /**
   * Per-recruiter override of the platform-default commission rates.
   * Empty by default — recruiters earn the rates in
   * `platformConfig/commissionRules` unless super-admin overrides them
   * here.
   */
  commissionOverrides: z
    .object({
      signupFeeShareRate: z.number().min(0).max(1).optional(),
      activationFeeShareRate: z.number().min(0).max(1).optional(),
      subscriptionShareRate: z.number().min(0).max(1).optional(),
      tierMultiplier: z.number().positive().optional(),
    })
    .default({}),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type RecruiterProfile = z.infer<typeof RecruiterProfileSchema>;

/**
 * `recruiters/{recruiterId}/agents/{agentId}` back-ref doc. Mirrors
 * `AgentOperatorBackref`: lets the recruiter PWA render "who did I
 * sign up" without cross-recruiter reads.
 *
 * Written by `recruiterBootstrap` on agent signup, patched by
 * `commissionAccrue` on every agent's operator-invoice that fans out
 * a recruiter accrual.
 */
export const RecruiterAgentBackrefSchema = z.object({
  agentId: z.string().min(1).optional().describe('Mirrors the doc id; convenience field.'),
  signedUpAt: TimestampSchema,
  /** Snapshot at signup; refreshed by status-sync triggers. */
  currentAgentStatus: RecruiterStatusSchema.optional(),
  /** Agent's name + contact, snapshotted so the recruiter PWA needs no cross-doc reads. */
  agentName: z.string().optional(),
  agentPhone: z.string().optional(),
  agentEmail: z.string().email().optional(),
  /** First-paid timestamp — the moment the recruiter's first commission for this agent accrued. */
  firstDownstreamPaidAt: TimestampSchema.nullable().optional(),
  /** Most-recent paid invoice that fanned out to this recruiter. */
  lastDownstreamPaidAt: TimestampSchema.optional(),
  /** Running total of recruiter commission earned through this agent. */
  lifetimeRecruiterEarningsSen: MoneySchema.default(0),
});
export type RecruiterAgentBackref = z.infer<typeof RecruiterAgentBackrefSchema>;

/**
 * `recruiterApplications/{appId}` — self-signup queue (mirrors
 * agentApplications). Super-admin moderates → on approval, a real
 * `recruiters/{id}` doc is created via Cloud Function.
 */
export const RecruiterApplicationSchema = z.object({
  id: ShortIdSchema,
  platformBrandId: SlugSchema,
  name: z.string().min(1),
  phone: z.string().min(1),
  email: z.string().email(),
  locale: LocaleSchema.default('en'),
  /** Free-form pitch the applicant wrote about themselves. */
  pitch: z.string().max(500).optional(),
  status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
  submittedAt: TimestampSchema,
  reviewedAt: TimestampSchema.optional(),
  reviewedBy: UidSchema.optional(),
  rejectionReason: z.string().optional(),
});
export type RecruiterApplication = z.infer<typeof RecruiterApplicationSchema>;

/**
 * `recruiters/{recruiterId}/payouts/{payoutId}` — Sprint 97. Mirror of
 * `AgentPayoutSchema`; one doc per super-admin-initiated payout batch
 * for this recruiter. Items array references the earnings-ledger entry
 * IDs the batch consumed (those entries get `payoutId` stamped).
 */
export const RecruiterPayoutSchema = z.object({
  id: ShortIdSchema,
  periodStart: TimestampSchema,
  periodEnd: TimestampSchema,
  items: z.array(ShortIdSchema),
  grossAmount: MoneySchema,
  status: z.enum(['pending', 'processing', 'paid', 'failed']),
  method: z.enum(['duitnow', 'bank_transfer']),
  destination: RecruiterPayoutDetailsSchema,
  initiatedAt: TimestampSchema,
  paidAt: TimestampSchema.optional(),
  txRef: z.string().optional(),
});
export type RecruiterPayout = z.infer<typeof RecruiterPayoutSchema>;
